/**
 * AlertsSummarySection
 * Summary cards showing alert counts by severity
 */

import { AlertTriangle, AlertCircle, Info, Wifi, Calculator, CreditCard } from 'lucide-react';
import { Card, CardContent } from '@/core/components/ui/card';
import { Skeleton } from '@/core/components/ui/skeleton';
import { AlertSummary } from '../../types/dashboard-types';
import { cn } from '@/core/lib/utils';

interface AlertsSummarySectionProps {
  summary: AlertSummary | null;
  isLoading?: boolean;
}

interface SummaryCardProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  bgColor: string;
  textColor: string;
  isLoading?: boolean;
}

function SummaryCard({ icon, label, count, bgColor, textColor, isLoading }: SummaryCardProps) {
  return (
    <Card className={cn('border-0', bgColor)}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className={cn('text-sm font-medium', textColor.replace('text-', 'text-opacity-80 text-'))}>{label}</p>
            <p className={cn('text-3xl font-bold mt-1', textColor)}>
              {isLoading ? <Skeleton className="h-9 w-12" /> : count}
            </p>
          </div>
          <div className={cn('p-3 rounded-lg bg-white/20', textColor)}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface TypeCardProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  isLoading?: boolean;
}

function TypeCard({ icon, label, count, isLoading }: TypeCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg text-gray-600">
            {icon}
          </div>
          <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="text-xl font-semibold text-gray-900">
              {isLoading ? <Skeleton className="h-6 w-8 inline-block" /> : count}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AlertsSummarySection({ summary, isLoading = false }: AlertsSummarySectionProps) {
  return (
    <div className="space-y-4">
      {/* Severity summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard
          icon={<AlertTriangle className="h-6 w-6" />}
          label="Total Active"
          count={summary?.totalActive || 0}
          bgColor="bg-gray-900"
          textColor="text-white"
          isLoading={isLoading}
        />
        <SummaryCard
          icon={<AlertCircle className="h-6 w-6" />}
          label="Critical"
          count={summary?.criticalCount || 0}
          bgColor="bg-red-600"
          textColor="text-white"
          isLoading={isLoading}
        />
        <SummaryCard
          icon={<AlertTriangle className="h-6 w-6" />}
          label="Warning"
          count={summary?.warningCount || 0}
          bgColor="bg-yellow-500"
          textColor="text-white"
          isLoading={isLoading}
        />
        <SummaryCard
          icon={<Info className="h-6 w-6" />}
          label="Info"
          count={summary?.infoCount || 0}
          bgColor="bg-blue-600"
          textColor="text-white"
          isLoading={isLoading}
        />
      </div>

      {/* Type breakdown cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TypeCard
          icon={<Wifi className="h-5 w-5" />}
          label="Connectivity Issues"
          count={summary?.connectivityIssues || 0}
          isLoading={isLoading}
        />
        <TypeCard
          icon={<Calculator className="h-5 w-5" />}
          label="Failed Calculations"
          count={summary?.failedCalculations || 0}
          isLoading={isLoading}
        />
        <TypeCard
          icon={<CreditCard className="h-5 w-5" />}
          label="Failed Payments"
          count={summary?.failedPayments || 0}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
